import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  COLLECTIONS_FINANCIAL_EFFICIENCY_FILTERS,
  COLLECTIONS_FINANCIAL_EFFICIENCY_KPI_DEFINITIONS,
} from '@constants/Reports/collectionsFinancialEfficiency.constants'
import {
  ReportsDistributionView,
  ReportsKpiMetric,
} from '@dataTypes/reports.type'
import { useCollectionsFinancialEfficiency } from '@hooks/reports/useCollectionsFinancialEfficiency'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import type { ReportPeriodBucket } from '@services/api/reports/finance.api'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  computeDynamicDollarAxis,
  formatKpiCurrency,
  formatKpiPercent,
} from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }
const REPORT_BAR_CATEGORY_GAP = '16%'
const REPORT_YAXIS_WIDTH = 56
const REPORT_PERCENTAGE_Y_AXIS = {
  domain: [0, 100] as [number, number],
  ticks: [0, 20, 40, 60, 80, 100],
  tickFormatter: (v: number) => `${v}%`,
}

function formatDollarLabel(value: number): string {
  if (value >= 10_000) {
    const inK = value / 1000
    const rounded = Math.round(inK * 100) / 100
    return `$${rounded}K`
  }
  if (value >= 1000) {
    const inK = value / 1000
    const rounded = Math.round(inK * 100) / 100
    return `$${rounded}K`
  }
  return `$${value}`
}

function formatDollarTooltip(value: number): string {
  return `$${value.toLocaleString('en-US')}`
}

function buildDollarSeries(
  rows: ReportPeriodBucket[],
  view: ReportsDistributionView,
): Array<{ month: string; barValue: number; topLabel: string }> {
  if (view === 'Count') {
    return rows.map((row) => ({
      month: row.label,
      barValue: row.value,
      topLabel: formatDollarLabel(row.value),
    }))
  }
  const total = rows.reduce((sum, r) => sum + r.value, 0) || 1
  return rows.map((row) => {
    const pct = Math.round((row.value / total) * 1000) / 10
    return {
      month: row.label,
      barValue: pct,
      topLabel: `${pct}%`,
    }
  })
}

function CollectionsFinancialEfficiencyReport(): React.ReactElement {
  const [collectionsView, setCollectionsView] =
    useState<ReportsDistributionView>('Count')
  const [sameDayView, setSameDayView] =
    useState<ReportsDistributionView>('Count')
  const [productionView, setProductionView] =
    useState<ReportsDistributionView>('Count')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const { loading, error, data, bucketLabel } =
    useCollectionsFinancialEfficiency(
      (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
        string,
        string
      >,
    )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage:
      'Failed to load collections financial efficiency data. Please try again.',
  })

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return COLLECTIONS_FINANCIAL_EFFICIENCY_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footer,
        value: loading ? '…' : '—',
      }))
    }

    const valueById: Record<string, number> = {
      collections: data.collections.value,
      production: data.production.value,
      'net-collection-percentage': data.netCollectionPercentage.value,
      'same-day-payments': data.sameDayPayments.value,
    }

    return COLLECTIONS_FINANCIAL_EFFICIENCY_KPI_DEFINITIONS.map((def) => ({
      id: def.id,
      title: def.title,
      footer: def.footer,
      value:
        def.format === 'percent'
          ? formatKpiPercent(valueById[def.id])
          : formatKpiCurrency(valueById[def.id]),
    }))
  }, [data, loading])

  const collectionsChartData = useMemo(
    () => buildDollarSeries(data?.collections.buckets ?? [], collectionsView),
    [data, collectionsView],
  )
  const collectionsYAxis = useMemo(() => {
    if (collectionsView === 'Percentage') return REPORT_PERCENTAGE_Y_AXIS
    return computeDynamicDollarAxis(
      collectionsChartData.map((row) => row.barValue),
    )
  }, [collectionsView, collectionsChartData])

  const netCollectionData = useMemo(
    () =>
      (data?.netCollectionPercentage.buckets ?? []).map((row) => ({
        month: row.label,
        barValue: row.value,
        topLabel: `${row.value}%`,
      })),
    [data],
  )

  const sameDayChartData = useMemo(
    () => buildDollarSeries(data?.sameDayPayments.buckets ?? [], sameDayView),
    [data, sameDayView],
  )
  const sameDayYAxis = useMemo(() => {
    if (sameDayView === 'Percentage') return REPORT_PERCENTAGE_Y_AXIS
    return computeDynamicDollarAxis(sameDayChartData.map((row) => row.barValue))
  }, [sameDayView, sameDayChartData])

  const productionChartData = useMemo(
    () => buildDollarSeries(data?.production.buckets ?? [], productionView),
    [data, productionView],
  )
  const productionYAxis = useMemo(() => {
    if (productionView === 'Percentage') return REPORT_PERCENTAGE_Y_AXIS
    return computeDynamicDollarAxis(
      productionChartData.map((row) => row.barValue),
    )
  }, [productionView, productionChartData])

  const renderDollarChart = (
    chartData: ReturnType<typeof buildDollarSeries>,
    axis: ReturnType<typeof computeDynamicDollarAxis>,
    view: ReportsDistributionView,
  ): React.ReactElement => (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={chartData}
        margin={REPORT_BAR_CHART_MARGIN}
        barCategoryGap={REPORT_BAR_CATEGORY_GAP}
      >
        <CartesianGrid
          vertical={false}
          strokeDasharray="0"
          stroke={REPORT_CHART_GRID_STROKE}
        />
        <XAxis
          dataKey="month"
          axisLine={false}
          tickLine={false}
          tick={REPORT_CHART_AXIS_TICK}
        />
        <YAxis
          domain={axis.domain}
          ticks={axis.ticks}
          tickFormatter={axis.tickFormatter}
          axisLine={false}
          tickLine={false}
          tick={REPORT_CHART_AXIS_TICK}
          width={REPORT_YAXIS_WIDTH}
        />
        <Tooltip
          formatter={(value: number) =>
            view === 'Count' ? formatDollarTooltip(value) : `${value}%`
          }
          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
        />
        <Bar
          dataKey="barValue"
          fill="var(--blue-400)"
          radius={[8, 8, 8, 8]}
          barSize={100}
        />
      </BarChart>
    </ResponsiveContainer>
  )

  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.collections.value,
      data?.production.value,
      data?.netCollectionPercentage.value,
      data?.sameDayPayments.value,
    ),
  )
  const collectionsEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.collections?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const netCollectionEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.netCollectionPercentage?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const sameDayEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.sameDayPayments?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const productionEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.production?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )

  return (
    <div className="reports-patient-base-health reports-treatment-conversion-funnel reports-collections-financial-efficiency">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Collections & Financial Efficiency">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={COLLECTIONS_FINANCIAL_EFFICIENCY_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      {kpiEmpty ? (
        <DashboardSectionEmptyState />
      ) : (
        <ReportKpiGrid items={kpiItems} />
      )}

      <div className="reports-treatment-conversion-funnel__stack">
        <div className="reports-treatment-conversion-funnel__grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Collections ({bucketLabel})
                </ReportSectionHeading>
                {collectionsEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={collectionsView}
                    onChange={(value) =>
                      setCollectionsView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {collectionsEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  {renderDollarChart(
                    collectionsChartData,
                    collectionsYAxis,
                    collectionsView,
                  )}
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Net Collection % ({bucketLabel})
                </ReportSectionHeading>
              </div>
              {netCollectionEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={netCollectionData}
                      margin={REPORT_BAR_CHART_MARGIN}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={REPORT_PERCENTAGE_Y_AXIS.domain}
                        ticks={REPORT_PERCENTAGE_Y_AXIS.ticks}
                        tickFormatter={REPORT_PERCENTAGE_Y_AXIS.tickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) => `${value}%`}
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Bar
                        dataKey="barValue"
                        fill="var(--blue-400)"
                        radius={[8, 8, 8, 8]}
                        barSize={100}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>

        <div className="reports-treatment-conversion-funnel__grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Same-day Payments ({bucketLabel})
                </ReportSectionHeading>
                {sameDayEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={sameDayView}
                    onChange={(value) =>
                      setSameDayView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {sameDayEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  {renderDollarChart(
                    sameDayChartData,
                    sameDayYAxis,
                    sameDayView,
                  )}
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Production Reference ({bucketLabel})
                </ReportSectionHeading>
                {productionEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={productionView}
                    onChange={(value) =>
                      setProductionView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {productionEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  {renderDollarChart(
                    productionChartData,
                    productionYAxis,
                    productionView,
                  )}
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>
      </div>
    </div>
  )
}

export default CollectionsFinancialEfficiencyReport
